<script setup>
import { computed } from 'vue'
import LightMeter from '@/components/LightMeter.vue'

const props = defineProps({
  product: { type: Object, required: true }
})

defineEmits([ 'open' ])

// The whole card is the button: on a phone, aiming at a small link inside the
// card costs more than tapping the card.
//
// It used to open WhatsApp straight away. That converted whoever already knew
// what they wanted and lost whoever was still looking, because there was
// nothing to look at. Now it opens the product sheet, and both ways to ask for
// a quote live in there, after the photos.
//
// How many photos there are is not the grid's business: the grid is for
// choosing by light and by looks. The count showed up as a badge over the photo
// and was one more thing to read in a card that already says enough.

// Placeholder for a product with no photo yet. It is a blind drawn from the one
// fact the card is organised around: light. Horizontal bands for the fabric, a
// wash from the top that grows with `light`, so a blackout panel reads dark and
// a sunscreen panel reads lit. It is plainly a drawing, not a photo that failed
// to load, and it disappears the moment `image` points at a file.
const glow = computed(() => 0.05 + (props.product.light / 5) * 0.3)
</script>

<template>
  <button
    type="button"
    class="group flex flex-col text-start"
    :aria-label="`Ver ${product.name}`"
    @click="$emit('open', product)"
  >
    <div class="relative overflow-hidden rounded-md bg-tobacco">
      <img
        v-if="product.image"
        :src="product.image"
        :alt="product.alt"
        :width="product.width"
        :height="product.height"
        loading="lazy"
        decoding="async"
        class="aspect-4/5 w-full object-cover transition-[transform,filter] duration-700 ease-soft group-hover:scale-[1.04]"
        :style="{ objectPosition: product.focus }"
      />
      <div
        v-else
        class="photo-slot aspect-4/5 w-full transition-transform duration-700 ease-soft group-hover:scale-[1.04]"
        :style="{ '--glow': glow }"
        aria-hidden="true"
      ></div>

      <div
        class="pointer-events-none absolute inset-0 ring-1 ring-inset ring-linen/10"
        aria-hidden="true"
      ></div>

      <!-- The call to action, on hover and on keyboard focus: the photo darkens
           and the button comes up over it. It is a span, not a button — the
           whole card is already the button, and a button inside a button is
           invalid markup. Touch users and screen readers lose nothing: they get
           the card, which does the same thing. -->
      <div
        class="pointer-events-none absolute inset-0 flex items-center justify-center bg-night/70 opacity-0 transition-opacity duration-500 ease-soft group-hover:opacity-100 group-focus-visible:opacity-100"
        aria-hidden="true"
      >
        <span
          class="inline-flex scale-95 items-center gap-2 rounded-full bg-night px-4 py-3 text-sm font-semibold text-linen shadow-xl shadow-night/50 ring-1 ring-inset ring-linen/15 transition-transform duration-500 ease-soft group-hover:scale-100 group-focus-visible:scale-100 sm:gap-2.5 sm:px-6 sm:py-3.5 sm:text-base"
        >
          <svg viewBox="0 0 24 24" fill="none" class="h-5 w-5" aria-hidden="true">
            <circle cx="11" cy="11" r="7" stroke="currentColor" stroke-width="2" />
            <path d="m16.5 16.5 4 4M11 8v6M8 11h6" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
          </svg>
          Ver detalles
        </span>
      </div>
    </div>

    <h3 class="display mt-4 text-lg text-linen sm:mt-5 sm:text-2xl">{{ product.name }}</h3>

    <!-- Hidden on the two-up phone grid: at that width the sentence runs to four
         lines and pushes the meters of a row out of line with each other. From
         sm up both lines are reserved, so every meter in a row sits on the same
         baseline whether the summary runs to one line or two. -->
    <p class="mt-1.5 hidden leading-snug text-glow/85 sm:block sm:min-h-[2lh]">{{ product.summary }}</p>

    <div class="mt-3 sm:mt-4">
      <LightMeter :value="product.light" :adjustable="!!product.adjustable" />
    </div>

    <p class="mt-4 hidden text-sm leading-relaxed text-linen/65 sm:block sm:min-h-[2lh]">
      {{ product.bestFor }}
    </p>

  </button>
</template>

<style scoped>
.photo-slot {
  background-color: var(--color-tobacco);
  background-image:
    linear-gradient(
      180deg,
      rgb(232 167 92 / var(--glow)) 0%,
      rgb(232 167 92 / calc(var(--glow) * 0.25)) 45%,
      transparent 78%
    ),
    repeating-linear-gradient(
      180deg,
      rgb(243 236 226 / 0.05) 0 1px,
      transparent 1px 13px
    );
}
</style>
